import * as React from 'react'

import FloatingLabelInput from './floating-label-input-animated'

interface DateInputProps
  extends Omit<React.ComponentPropsWithoutRef<typeof FloatingLabelInput>, 'value' | 'onChangeText'> {
  error?: boolean
  value?: Date | null
  onChangeText: (date: Date | null) => void
}

const DateInput = React.forwardRef<React.ComponentRef<typeof FloatingLabelInput>, DateInputProps>(
  ({ onChangeText, value, ...props }, ref) => {
    const [inputValue, setInputValue] = React.useState('')

    // Convert Date to display string
    const formatDateToString = (date: Date | null): string => {
      if (!date) return ''
      const _date = new Date(date)
      const month = (_date.getMonth() + 1).toString().padStart(2, '0')
      const day = _date.getDate().toString().padStart(2, '0')
      const year = _date.getFullYear()
      return `${month}/${day}/${year}`
    }

    // Update input value when value prop changes
    React.useEffect(() => {
      if (!value) return
      setInputValue(formatDateToString(value || null))
    }, [value])

    const handleChangeText = (text: string) => {
      // Remove any non-numeric characters
      let numericOnly = text.replace(/[^0-9]/g, '')

      // Format as mm/dd/yyyy
      let formatted = ''
      if (numericOnly.length > 0) {
        // Handle month
        const month = numericOnly.slice(0, 2)
        if (parseInt(month) > 12) {
          numericOnly = '12' + numericOnly.slice(2)
        }
        formatted = month

        // Add day
        if (numericOnly.length > 2) {
          const day = numericOnly.slice(2, 4)
          if (parseInt(day) > 31) {
            numericOnly = numericOnly.slice(0, 2) + '31' + numericOnly.slice(4)
          }
          formatted = `${formatted}/${day}`
        }

        // Add year
        if (numericOnly.length > 4) {
          formatted = `${formatted}/${numericOnly.slice(4, 8)}`
        }
      }

      // Add slashes automatically
      if (text.length === 2 && !text.includes('/')) {
        formatted = text + '/'
      } else if (text.length === 5 && text.split('/').length === 2) {
        formatted = text + '/'
      }

      // Limit to mm/dd/yyyy format (10 characters)
      formatted = formatted.slice(0, 10)

      // Update local input state
      setInputValue(formatted)

      // Convert to Date object if we have a complete date
      if (formatted.length === 10) {
        const [month, day, year] = formatted.split('/').map((num) => parseInt(num))
        const date = new Date(year, month - 1, day)
        if (!isNaN(date.getTime())) {
          // Check if date is valid
          onChangeText(date)
        }
      } else if (formatted.length === 0) {
        onChangeText(null)
      }
    }

    return (
      <FloatingLabelInput
        {...props}
        label="mm/dd/yyyy"
        ref={ref}
        value={inputValue}
        onChangeText={handleChangeText}
        maxLength={10}
      />
    )
  },
)

DateInput.displayName = 'DateInput'

export default DateInput
